import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/components/ui/Icon';
import { api, ApiError, type Project } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export function ProjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, signOut } = useAuth();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.projects.list(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => api.projects.create(name),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/editor/${project.id}`);
    },
  });

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    createMutation.mutate(newName.trim());
    setNewName('');
    setCreating(false);
  }

  return (
    <div className="h-screen overflow-y-auto bg-bg-base text-text">
      <header className="bg-bg-panel border-b border-border h-[52px] flex items-center px-4 gap-4 sticky top-0 z-10">
        <Link to="/projects" className="flex items-center gap-2.5 font-semibold text-[15px]">
          <div className="w-7 h-7 rounded-[7px] flex items-center justify-center bg-gradient-to-br from-accent to-ai shadow">
            <Icon name="clapperboard" size={16} className="text-white" />
          </div>
          <span>topStudio</span>
        </Link>
        <div className="ml-auto flex items-center gap-2 text-sm text-text-muted">
          <span className="hidden md:inline">{user?.email}</span>
          <button onClick={signOut} className="btn btn-ghost" title="Logga ut">
            <Icon name="log-out" size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold">Mina projekt</h1>
            <p className="text-text-muted text-sm mt-1">
              Skapa ett nytt projekt eller fortsatt med ett befintligt
            </p>
          </div>
          <button
            onClick={() => setCreating(true)}
            className="btn btn-primary"
            disabled={createMutation.isPending}
          >
            <Icon name="plus" size={14} />
            Nytt projekt
          </button>
        </div>

        {creating && (
          <form
            onSubmit={handleCreate}
            className="mb-6 p-4 bg-bg-panel border border-border rounded-lg flex items-center gap-2"
          >
            <Icon name="folder-plus" size={18} className="text-accent" />
            <input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Namn pa projektet (t.ex. Reseberattelse Madrid)"
              className="flex-1 bg-transparent border-0 focus:outline-none text-sm placeholder:text-text-subtle"
            />
            <button type="submit" disabled={!newName.trim() || createMutation.isPending} className="btn btn-primary">
              {createMutation.isPending ? (
                <Icon name="loader-2" size={14} className="animate-spin" />
              ) : (
                'Skapa'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setNewName('');
              }}
              className="btn btn-ghost"
            >
              Avbryt
            </button>
          </form>
        )}

        {error instanceof ApiError && error.status === 401 && (
          <div className="text-text-muted text-sm">
            Sessionen har gatt ut. <Link to="/login" className="text-accent">Logga in igen</Link>.
          </div>
        )}

        {isLoading && (
          <div className="text-text-muted text-sm flex items-center gap-2">
            <Icon name="loader-2" size={14} className="animate-spin" />
            Laddar projekt...
          </div>
        )}

        {!isLoading && data?.projects.length === 0 && !creating && (
          <div className="text-center py-16 border-2 border-dashed border-border rounded-xl">
            <div className="text-text-muted text-sm mb-3">Du har inga projekt an</div>
            <button onClick={() => setCreating(true)} className="btn btn-primary">
              <Icon name="plus" size={14} />
              Skapa ditt forsta projekt
            </button>
          </div>
        )}

        {data?.projects && data.projects.length > 0 && (
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.projects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function ProjectCard({ project }: { project: Project }) {
  return (
    <li>
      <Link
        to={`/editor/${project.id}`}
        className="block p-5 bg-bg-panel border border-border rounded-xl hover:border-accent hover:bg-bg-panel-hover transition-all"
      >
        <div className="aspect-video bg-bg-elevated rounded-lg mb-3 flex items-center justify-center text-text-subtle">
          <Icon name="film" size={32} />
        </div>
        <div className="font-semibold text-sm truncate">{project.name}</div>
        <div className="text-xs text-text-muted mt-1 truncate">
          {project.description ?? 'Ingen beskrivning'}
        </div>
        <div className="text-xs text-text-subtle mt-2">
          Uppdaterad {new Date(project.updated_at).toLocaleString('sv-SE')}
        </div>
      </Link>
    </li>
  );
}
